<script setup>
import AddSteamGameView from "@/views/AddSteamGameView.vue";
import { useEventsStore } from "@/stores/events.js";
import { useUserDataStore } from "@/stores/userData";
import { useOwnedGamesStore } from "@/stores/ownedGamesStore.js";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";
import Footer from "@/components/Footer.vue";
import HeaderView from "@/views/HeaderView.vue";
import Navigation from "@/components/Navigation.vue";
import Card from "@/components/ui/card/Card.vue";
import { stringToTrigrams } from "@/lib/stringUtils.js";

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("lg");

const props = defineProps({
  fromInterests: {
    type: Boolean,
    default: false
  }
})

const eventsStore = useEventsStore();
const userDataStore = useUserDataStore();
const ownedGamesStore = useOwnedGamesStore();
const route = useRoute();

const currentEventId = route.params.id;
const userId = computed(() => userDataStore.steamId);
const ownedGames = computed(() => ownedGamesStore.getOwnedGames());
const eventProfiles = computed(() => eventsStore.profiles);

const sortMode = ref("");

const event = computed(() =>
  eventsStore.events.find((e) => e.id === currentEventId)
);

const interests = computed(() =>
  event.value ? eventsStore.getInterests(event.value) : []
);

function addUserToInterest(interest) {
  if (!event.value) return;
  eventsStore.addUserToInterest(event?.value.id, interest, userId.value);
}

function removeUserFromInterest(interest) {
  if (!event.value) return;
  eventsStore.removeUserFromInterest(
    event?.value.id,
    interest.id,
    userId.value
  );
}

//need to update this to be of form game, not interest
function toggleInterest(game) {
  const interest = {
    id: String(game.id),
    name: game.name,
    imageURL: `https://media.steampowered.com/steamcommunity/public/images/apps/${game.id}/${game.icon}.jpg`,
  };
  if (
    interests.value
      .find((interest) => interest.id == game.id)
      ?.users?.includes(userId.value)
  ) {
    removeUserFromInterest(interest);
  } else {
    addUserToInterest(interest);
  }
}

function changeSortMode(mode) {
  sortMode.value = mode;
}

const sortedGames = computed(() => {
  if (sortMode.value == "playtime") {
    return ownedGamesStore.getOwnedGamesByPlaytime();
  } else if (sortMode.value == "lastPlayed") {
    return ownedGamesStore.getOwnedGamesByLastPlayed();
  } else {
    return ownedGamesStore.getOwnedGamesSortedByName();
  }
});

const searchQuery = ref("");
function setSearch(value) {
  searchQuery.value = value;
}

const filteredGames = computed(() => {
  if (!searchQuery.value || searchQuery.value.trim() === "") {
    return sortedGames.value;
  }

  return sortedGames.value.filter((game) => {
    return game.game.name
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase());
  });
});
</script>

<template>
  <Card
    class="border-0 rounded-0 shadow-none p-4 w-full h-full min-h-svh bg-background pb-50"
  >
    <div v-if="!isMobile && !fromInterests" class="w-full h-20">
      <Navigation :username="userDataStore.userProfile?.nickname" :avatarUrl="userDataStore.userProfile.avatarUrl"></Navigation>
    </div>
    <HeaderView v-if="isMobile" name="Add Your Games"></HeaderView>
    <AddSteamGameView
      :interests="interests"
      :uid="userId"
      :ownedGames="filteredGames"
      :sortMode="sortMode"
      :search="searchQuery"
      @toggleInterest="toggleInterest"
      @changeSortMode="changeSortMode"
      @changeSearch="setSearch"
      :isMobile="isMobile"
      :profiles="eventProfiles"
    ></AddSteamGameView>
  </Card>
  <Footer v-if="isMobile"></Footer>
</template>
